/**
 * Chart Context Registry
 *
 * Central allowlist of valid context groups and chart names.
 * All chart context identifiers MUST be declared here to prevent
 * duplicate or arbitrary strings scattered across components.
 *
 * When adding a new chart to the Advisor integration:
 * 1. Add a new entry to CHART_CONTEXT_GROUPS (if the section is new) — both
 *    `name` and `description` are required; TypeScript will error if either is missing.
 * 2. Add chart name entries to CHART_NAMES
 * 3. Use the constants in the component that creates the chartContextId prop
 *    and in the layout that sets contextGroup on BeaconChartWithLegend
 *
 * @see ARCHITECTURE.md for full design documentation.
 */

// ---------------------------------------------------------------------------
// Context Groups (sections)
// ---------------------------------------------------------------------------

/**
 * Valid context group definitions.
 * Each entry holds a `name` (the string ID used at runtime) and a `description`
 * (sent with the Ask Advisor payload). Both fields are mandatory — TypeScript
 * enforces this at the definition site so a missing description is a compile error.
 *
 * Layouts use `.name` when passing to ChartContextProvider / ChartContextGroupProvider:
 *   <ChartContextProvider group={CHART_CONTEXT_GROUPS.TRAFFIC.name}>
 *
 * Use `getChartContextGroup(id)` to resolve a group object from a runtime string ID.
 */
export const CHART_CONTEXT_GROUPS = {
  /** Traffic section: donut + comparison/current bar charts */
  TRAFFIC: { name: 'traffic', description: 'Donut and bar charts for total traffic' },
  /** Total Traffic section: spline chart showing total traffic over time */
  TOTAL_TRAFFIC: { name: 'total-traffic', description: 'Spline chart for total traffic over time' },
  /** Share of Total Traffic section: spline chart showing market share % */
  SHARE_OF_TRAFFIC: { name: 'share-of-traffic', description: 'Spline chart for share of total traffic over time' },
  /** Traffic by Keyword section: column chart grouped by keyword */
  TRAFFIC_BY_KEYWORD: { name: 'traffic-by-keyword', description: 'Column chart for traffic grouped by keyword' },
  /** Branded/Unbranded Traffic section: spline chart for branded vs unbranded share */
  BRANDED_UNBRANDED_TRAFFIC: {
    name: 'branded-unbranded-traffic',
    description: 'Spline chart for branded vs unbranded traffic share'
  },
  /** Top Entity Traffic section: column chart with double dropdown (entity + metric) */
  TRAFFIC_TOP_ENTITY: {
    name: 'traffic-top-entity',
    description: 'Column chart for top entity traffic with double dropdown'
  },
  /** Brand → Category/Subcategory top entity chart with double dropdown */
  TRAFFIC_TOP_ENTITY_BRAND: {
    name: 'traffic-top-entity-brand',
    description: 'Column chart for brand traffic by category/subcategory'
  },
  /** Filterable Stacked Bar section (keyword): stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR: {
    name: 'filterable-stacked-bar',
    description: 'Filterable stacked bar chart for traffic by keyword'
  },
  /** Filterable Stacked Bar section (category/subcategory): stacked/grouped column chart */
  FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for traffic by category/subcategory'
  },
  /** Keyword Count with Total Traffic section: spline chart showing keyword count over time */
  KEYWORD_COUNT_TRAFFIC: {
    name: 'keyword-count-traffic',
    description: 'Spline chart for keyword count with total traffic over time'
  },

  // ── Organic Traffic V2 sections ──

  /** Organic Traffic spline chart */
  ORGANIC_V2_TRAFFIC: { name: 'organic-v2-traffic', description: 'Spline chart for organic traffic over time' },
  /** Share of Organic Traffic spline chart */
  ORGANIC_V2_SHARE: { name: 'organic-v2-share', description: 'Spline chart for share of organic traffic over time' },
  /** Keyword single dropdown top entity chart */
  ORGANIC_V2_TOP_ENTITY_KEYWORD: {
    name: 'organic-v2-top-entity-keyword',
    description: 'Column chart for organic traffic by keyword (single dropdown)'
  },
  /** Double dropdown top entity chart (category/brand) */
  ORGANIC_V2_TOP_ENTITY: {
    name: 'organic-v2-top-entity',
    description: 'Column chart for organic traffic top entity with double dropdown'
  },
  /** Filterable stacked bar chart (keyword) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_KEYWORD: {
    name: 'organic-v2-filterable-stacked-bar-keyword',
    description: 'Filterable stacked bar chart for organic traffic by keyword'
  },
  /** Keyword Count with Organic Traffic spline chart */
  ORGANIC_V2_KEYWORD_COUNT: {
    name: 'organic-v2-keyword-count',
    description: 'Spline chart for keyword count with organic traffic over time'
  },
  /** Double dropdown top entity chart (company-level) */
  ORGANIC_V2_TOP_ENTITY_COMPANY: {
    name: 'organic-v2-top-entity-company',
    description: 'Column chart for organic traffic top entity (company-level)'
  },
  /** Double dropdown top entity chart (brand → category/subcategory) */
  ORGANIC_V2_TOP_ENTITY_BRAND: {
    name: 'organic-v2-top-entity-brand',
    description: 'Column chart for organic traffic by brand category/subcategory'
  },
  /** Branded/Unbranded organic traffic spline chart */
  ORGANIC_V2_BRANDED_UNBRANDED: {
    name: 'organic-v2-branded-unbranded',
    description: 'Spline chart for branded vs unbranded organic traffic share'
  },
  /** Filterable stacked bar chart (category/subcategory) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'organic-v2-filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for organic traffic by category/subcategory'
  },

  // ── Paid Traffic sections ──

  /** Paid Search Traffic spline chart */
  PAID_TRAFFIC: { name: 'paid-traffic', description: 'Spline chart for paid search traffic over time' },
  /** Paid Search Spend spline chart */
  PAID_TRAFFIC_AD_SPEND: { name: 'paid-traffic-ad-spend', description: 'Spline chart for paid search spend over time' },
  /** Share of Paid Search Traffic spline chart */
  PAID_TRAFFIC_SHARE: {
    name: 'paid-traffic-share',
    description: 'Spline chart for share of paid search traffic over time'
  },
  /** Top Entity Paid Traffic column chart with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY: {
    name: 'paid-traffic-top-entity',
    description: 'Column chart for top entity paid traffic with double dropdown'
  },
  /** Products with Paid Search Traffic spline chart */
  PAID_TRAFFIC_PRODUCTS: {
    name: 'paid-traffic-products',
    description: 'Spline chart for products with paid search traffic over time'
  },
  /** Keyword Count with Paid Search Traffic spline chart */
  PAID_TRAFFIC_KEYWORD_COUNT: {
    name: 'paid-traffic-keyword-count',
    description: 'Spline chart for keyword count with paid search traffic over time'
  },
  /** Branded/Unbranded Paid Traffic spline chart */
  PAID_TRAFFIC_BRANDED_UNBRANDED: {
    name: 'paid-traffic-branded-unbranded',
    description: 'Spline chart for branded vs unbranded paid traffic share'
  },
  /** Filterable Stacked Bar for paid traffic by keyword */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR: {
    name: 'paid-traffic-filterable-stacked-bar',
    description: 'Filterable stacked bar chart for paid traffic by keyword'
  },
  /** Brand → Category/Subcategory top entity chart for paid traffic */
  PAID_TRAFFIC_TOP_ENTITY_BRAND: {
    name: 'paid-traffic-top-entity-brand',
    description: 'Column chart for brand paid traffic by category/subcategory'
  },
  /** Filterable Stacked Bar for paid traffic by category/subcategory */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'paid-traffic-filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for paid traffic by category/subcategory'
  },

  // ── Other Traffic sections ──

  /** Other Traffic spline chart */
  OTHER_TRAFFIC: { name: 'other-traffic', description: 'Spline chart for other traffic over time' },
  /** Products with Other Traffic spline chart */
  OTHER_TRAFFIC_PRODUCTS: {
    name: 'other-traffic-products',
    description: 'Spline chart for products with other traffic over time'
  },

  // ── Sales Summary sections ──

  /** Retail Sales spline chart (1P/3P selector in 1P branch, plain in non-1P branch) */
  RETAIL_SALES: { name: 'retail-sales', description: 'Spline chart for retail sales over time' },
  /** Market Share of Retail Sales spline chart (multiple field options) */
  MARKET_SHARE_RETAIL_SALES: {
    name: 'market-share-retail-sales',
    description: 'Spline chart for market share of retail sales over time'
  },
  /** Product Count spline chart */
  SALES_PRODUCT_COUNT: { name: 'sales-product-count', description: 'Spline chart for product count over time' },
  /** 1P Retail Sales as % of Total spline chart */
  SALES_1P_PERCENT: {
    name: 'sales-1p-percent',
    description: 'Spline chart for 1P retail sales as percentage of total'
  },
  /** Market Share column chart (both 1P and non-1P variants) */
  SALES_MARKET_SHARE_COLUMN: {
    name: 'sales-market-share-column',
    description: 'Column chart for market share of retail sales by entity'
  },
  /** Top entity double dropdown chart (company-level, 1P branch) */
  SALES_TOP_ENTITY: {
    name: 'sales-top-entity',
    description: 'Column chart for top entity retail sales with double dropdown'
  },
  /** Top entity double dropdown chart (category/brand/etc. and non-1P) */
  SALES_TOP_ENTITY_BROAD: {
    name: 'sales-top-entity-broad',
    description: 'Column chart for top entity retail sales by category/brand'
  },
  /** Product market share spline chart (product entity only) */
  SALES_MARKET_SHARE_PRODUCT: {
    name: 'sales-market-share-product',
    description: 'Spline chart for product market share of retail sales'
  },

  // ── Conversion sections ──

  /** Conversion rate spline chart */
  CONVERSION_SPLINE: { name: 'conversion-spline', description: 'Spline chart for conversion rate over time' },
  /** Conversion top entity column chart */
  CONVERSION_TOP_ENTITY: {
    name: 'conversion-top-entity',
    description: 'Column chart for conversion rate by top entity'
  },

  // ── Price Summary sections ──

  /** Retail Price spline chart */
  PRICE_RETAIL_PRICE: { name: 'price-retail-price', description: 'Spline chart for retail price over time' },
  /** Price Index spline chart */
  PRICE_INDEX: { name: 'price-index', description: 'Spline chart for price index over time' },
  /** Price top entity column chart */
  PRICE_TOP_ENTITY: {
    name: 'price-top-entity',
    description: 'Column chart for price by top entity with double dropdown'
  },

  // ── Price Promotion sections ──

  /** Promotions top section: donut chart + spline chart */
  PROMOTION_TOP_SECTION: {
    name: 'promotion-top-section',
    description: 'Donut and spline charts for promotion count'
  },
  /** Promotions top entity column chart (entity dropdown) */
  PROMOTION_TOP_ENTITY: { name: 'promotion-top-entity', description: 'Column chart for promotions by top entity' },
  /** Promotions deal type column chart (deal type dropdown) */
  PROMOTION_DEAL_TYPE: { name: 'promotion-deal-type', description: 'Column chart for promotions by deal type' },

  // ── Units Sold sections ──

  /** Units Sold spline chart (1P/3P or plain) */
  UNITS_SOLD_SPLINE: { name: 'units-sold-spline', description: 'Spline chart for units sold over time' },
  /** Market Share of Units Sold spline chart */
  UNITS_SOLD_MARKET_SHARE: {
    name: 'units-sold-market-share',
    description: 'Spline chart for market share of units sold over time'
  },
  /** Product Count spline chart (units sold context) */
  UNITS_SOLD_PRODUCT_COUNT: {
    name: 'units-sold-product-count',
    description: 'Spline chart for product count (units sold context)'
  },
  /** 1P Total Units Sold spline chart (company-level) */
  UNITS_SOLD_1P_TOTAL: {
    name: 'units-sold-1p-total',
    description: 'Spline chart for 1P total units sold (company-level)'
  },
  /** Market Share column chart for units sold */
  UNITS_SOLD_MARKET_SHARE_COLUMN: {
    name: 'units-sold-market-share-column',
    description: 'Column chart for market share of units sold by entity'
  },
  /** Top entity double dropdown chart for units sold (company) */
  UNITS_SOLD_TOP_ENTITY: {
    name: 'units-sold-top-entity',
    description: 'Column chart for top entity units sold with double dropdown (company)'
  },
  /** Top entity double dropdown chart for units sold (broad) */
  UNITS_SOLD_TOP_ENTITY_BROAD: {
    name: 'units-sold-top-entity-broad',
    description: 'Column chart for top entity units sold with double dropdown (broad)'
  },
  /** 1P Total Units Sold spline chart (broad entities) */
  UNITS_SOLD_1P_TOTAL_BROAD: {
    name: 'units-sold-1p-total-broad',
    description: 'Spline chart for 1P total units sold (broad entities)'
  },
  /** Product market share spline chart for units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT: {
    name: 'units-sold-market-share-product',
    description: 'Spline chart for product market share of units sold'
  },
  /** Non-1P market share spline chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE: {
    name: 'units-sold-non1p-market-share',
    description: 'Spline chart for non-1P market share of units sold'
  },
  /** Non-1P market share column chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN: {
    name: 'units-sold-non1p-market-share-column',
    description: 'Column chart for non-1P market share of units sold by entity'
  },
  /** Non-1P top entity double dropdown chart for units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY: {
    name: 'units-sold-non1p-top-entity',
    description: 'Column chart for non-1P top entity units sold with double dropdown'
  }
} as const;

/** Full group object type — a union of all `{ name, description }` entries. */
export type ChartContextGroup = (typeof CHART_CONTEXT_GROUPS)[keyof typeof CHART_CONTEXT_GROUPS];

/** String union of all valid group IDs (the `.name` field of each group). */
export type ChartContextGroupId = ChartContextGroup['name'];

/**
 * Look up a context group object by its runtime string ID.
 * Returns `undefined` when the ID is not registered.
 */
export function getChartContextGroup(id: string): ChartContextGroup | undefined {
  return (Object.values(CHART_CONTEXT_GROUPS) as ChartContextGroup[]).find((g) => g.name === id);
}

// ---------------------------------------------------------------------------
// Chart Names
// ---------------------------------------------------------------------------

/**
 * Valid chart name identifiers.
 * Each name uniquely identifies a chart within the app.
 * These are combined with the app prefix, context group, and URL suffix
 * to form the full chart ID stored in the Zustand store / DynamoDB.
 *
 * Full ID format: {app}#{contextGroup}#{chartName}#{urlSuffix}
 * Example: atlas#traffic#traffic-donut#category/1280?rid=1&tab=trafficV2&subtab=totalClicks
 */
export const CHART_NAMES = {
  /** Donut chart showing total traffic breakdown (organic, paid, other) */
  TRAFFIC_DONUT: 'traffic-donut',
  /** Bar chart for the comparison time period */
  TRAFFIC_BAR_COMPARISON: 'traffic-bar-comparison',
  /** Bar chart for the current time period */
  TRAFFIC_BAR_CURRENT: 'traffic-bar-current',
  /** Spline chart showing total traffic over time */
  TOTAL_TRAFFIC_SPLINE: 'total-traffic-spline',
  /** Spline chart showing share of total traffic over time */
  SHARE_OF_TRAFFIC_SPLINE: 'share-of-traffic-spline',
  /** Column chart showing traffic grouped by keyword */
  TRAFFIC_BY_KEYWORD_COLUMN: 'traffic-by-keyword-column',
  /** Spline chart showing branded vs unbranded traffic share */
  BRANDED_UNBRANDED_SPLINE: 'branded-unbranded-spline',
  /** Column chart showing top entity traffic with double dropdown */
  TRAFFIC_TOP_ENTITY_COLUMN: 'traffic-top-entity-column',
  /** Stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR_CHART: 'filterable-stacked-bar-chart',
  /** Spline chart showing keyword count with total traffic over time */
  KEYWORD_COUNT_TRAFFIC_SPLINE: 'keyword-count-traffic-spline',

  // ── Organic Traffic V2 charts ──

  /** Spline chart for organic traffic over time */
  ORGANIC_V2_TRAFFIC_SPLINE: 'organic-v2-traffic-spline',
  /** Spline chart for share of organic traffic over time */
  ORGANIC_V2_SHARE_SPLINE: 'organic-v2-share-spline',
  /** Column chart for organic traffic top entity */
  ORGANIC_V2_TOP_ENTITY_COLUMN: 'organic-v2-top-entity-column',
  /** Spline chart for keyword count with organic traffic over time */
  ORGANIC_V2_KEYWORD_COUNT_SPLINE: 'organic-v2-keyword-count-spline',

  // ── Paid Traffic charts ──

  /** Spline chart for paid search traffic over time */
  PAID_TRAFFIC_SPLINE: 'paid-traffic-spline',
  /** Spline chart for paid search spend over time */
  PAID_TRAFFIC_AD_SPEND_SPLINE: 'paid-traffic-ad-spend-spline',
  /** Spline chart for share of paid search traffic over time */
  PAID_TRAFFIC_SHARE_SPLINE: 'paid-traffic-share-spline',
  /** Column chart for top entity paid traffic with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY_COLUMN: 'paid-traffic-top-entity-column',
  /** Spline chart for products with paid search traffic over time */
  PAID_TRAFFIC_PRODUCTS_SPLINE: 'paid-traffic-products-spline',
  /** Spline chart for keyword count with paid search traffic over time */
  PAID_TRAFFIC_KEYWORD_COUNT_SPLINE: 'paid-traffic-keyword-count-spline',

  // ── Other Traffic charts ──

  /** Spline chart for other traffic over time */
  OTHER_TRAFFIC_SPLINE: 'other-traffic-spline',
  /** Spline chart for products with other traffic over time */
  OTHER_TRAFFIC_PRODUCTS_SPLINE: 'other-traffic-products-spline',

  // ── Sales Summary charts ──

  /** Spline chart for retail sales (1P/3P selector or plain) */
  RETAIL_SALES_SPLINE: 'retail-sales-spline',
  /** Spline chart for market share of retail sales */
  MARKET_SHARE_RETAIL_SALES_SPLINE: 'market-share-retail-sales-spline',
  /** Spline chart for product count */
  SALES_PRODUCT_COUNT_SPLINE: 'sales-product-count-spline',
  /** Spline chart for 1P retail sales as % of total */
  SALES_1P_PERCENT_SPLINE: 'sales-1p-percent-spline',
  /** Column chart for market share of retail sales by entity */
  SALES_MARKET_SHARE_COLUMN_CHART: 'sales-market-share-column-chart',
  /** Column chart for top entity retail sales with double dropdown */
  SALES_TOP_ENTITY_COLUMN: 'sales-top-entity-column',
  /** Spline chart for product market share of retail sales */
  SALES_MARKET_SHARE_PRODUCT_SPLINE: 'sales-market-share-product-spline',

  // ── Conversion charts ──

  /** Spline chart for conversion rate over time */
  CONVERSION_SPLINE_CHART: 'conversion-spline-chart',
  /** Column chart for conversion rate by top entity */
  CONVERSION_TOP_ENTITY_COLUMN: 'conversion-top-entity-column',

  // ── Price Summary charts ──

  /** Spline chart for retail price over time */
  PRICE_RETAIL_PRICE_SPLINE: 'price-retail-price-spline',
  /** Spline chart for price index over time */
  PRICE_INDEX_SPLINE: 'price-index-spline',
  /** Column chart for price by top entity */
  PRICE_TOP_ENTITY_COLUMN: 'price-top-entity-column',

  // ── Price Promotion charts ──

  /** Spline chart for promotions overview */
  PROMOTION_TOP_SECTION_SPLINE: 'promotion-top-section-spline',
  /** Column chart for promotions by top entity */
  PROMOTION_TOP_ENTITY_COLUMN: 'promotion-top-entity-column',
  /** Column chart for promotions by deal type */
  PROMOTION_DEAL_TYPE_COLUMN: 'promotion-deal-type-column',

  // ── Units Sold charts ──

  /** Spline chart for units sold over time */
  UNITS_SOLD_SPLINE_CHART: 'units-sold-spline-chart',
  /** Spline chart for market share of units sold */
  UNITS_SOLD_MARKET_SHARE_SPLINE: 'units-sold-market-share-spline',
  /** Spline chart for 1P total units sold */
  UNITS_SOLD_1P_TOTAL_SPLINE: 'units-sold-1p-total-spline',
  /** Column chart for market share of units sold by entity */
  UNITS_SOLD_MARKET_SHARE_COLUMN_CHART: 'units-sold-market-share-column-chart',
  /** Column chart for top entity units sold */
  UNITS_SOLD_TOP_ENTITY_COLUMN: 'units-sold-top-entity-column',
  /** Spline chart for product market share of units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT_SPLINE: 'units-sold-market-share-product-spline',
  /** Spline chart for non-1P market share of units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_SPLINE: 'units-sold-non1p-market-share-spline',
  /** Column chart for non-1P market share of units sold by entity */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN_CHART: 'units-sold-non1p-market-share-column-chart',
  /** Column chart for non-1P top entity units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY_COLUMN: 'units-sold-non1p-top-entity-column'
} as const;

export type ChartName = (typeof CHART_NAMES)[keyof typeof CHART_NAMES];
